// @ts-nocheck
import React, { useEffect, useCallback } from "react";
import { useQueryClient } from "react-query";
import {
	AreaSeries,
	BarSeries,
	ColumnSeries,
	StackingAreaSeries,
	SplineAreaSeries,
	StackingColumnSeries,
	PieSeries,
} from "@syncfusion/ej2-react-charts";

import useTheme from "hooks/useTheme";
import NumberChart from "../Number/NumberChart";
import TargetChart from "../TargetChart/TargetChart";
import AccumulationChart from "../AccumulationChart/AccumulationChart";
import GaugeComponent from "../GaugeChart/GaugeChart";
import BasicChart from "../BasicChart/BasicChart";
import useUnread from "classes/Accounts/hooks/useUnread";
import { NewLabel } from "components/Tags/Label/TagLabel";
import genRequest from "includes/request";
import { QueryTagsList } from "components/Tags/TagsList/TagsList";
import { bar_chart_nbr_cols } from "../../../classes/Charts/Types/displayer/bar_charts_displayer";

export default function ChartTile(props) {
	const { theme } = useTheme();
	const { isReaded, setAsReaded } = useUnread();
	const QCL = useQueryClient();

	let chart = props.data;
	let readed_chart = isReaded("NotViewedChart", chart.id);

	const incrementViews = useCallback(() => {
		// The editor preview passes a temporary uuid as `id` (DOM id only).
		if (!chart?.id || props.editing) return Promise.resolve(true);
		// Don't gate on readed_chart: sessionStorage may be stale (set by
		// an older build that never told the backend), so we always
		// retry the API call. Backend is idempotent (ON CONFLICT).
		let req = genRequest(
			"charts/" + chart.id + "/increment-view",
			null,
			"put"
		)
			.then((resp) => {
				return resp;
			})
			.catch(() => { });
		return req;
	}, [chart, props.editing]);

	const handleClick = useCallback(() => {
		incrementViews().then(() => {
			setAsReaded("NotViewedChart", chart.id);
			QCL.invalidateQueries(["NotViewed", "Me", "NotViewedChart"]);
		});
	}, [incrementViews, setAsReaded, chart, QCL]);

	useEffect(() => {
		if (!isReaded("NotViewedChart", chart.id)) handleClick();
	}, [isReaded, chart, handleClick]);

	let values;

	try {
		values = JSON.parse(chart.chartValues);
		chart.parsedValues = values;
	} catch (e) {
		return false;
	}
	if (!values) return false;
	let specs = getChartSpecs(chart, chart.chartTypeId, values);
	let component = getComponent(theme, chart, specs, values);

	if (!component) return false;
	return (
		<div
			className={
				"tile title-form chart-tile fixed-tile" +
				(props.className ? " " + props.className : "") +
				(specs.nbrVals > 3 ? " w-100" : "")
			}
			onClick={handleClick}
		>
			<div className="d-flex align-items-center justify-content-center">
				{!readed_chart && !props.editing && (
					<NewLabel className="me-1" />
				)}
				<div className="font-bold text-center">{chart.title}</div>
			</div>
			{/* A new chart's editor preview has a temporary uuid as `id` */}
			{Number.isInteger(chart.id) && (
				<QueryTagsList
					queryKey={["Categories", "Chart", chart.id]}
					queryFn={() => genRequest("categories/chart/" + chart.id)}
				/>
			)}
			<div className="w-100">{component}</div>
			<SyncfusionFix chartId={chart.id} />
		</div>
	);
}

function SyncfusionFix(chartId) {
	let elem = document.getElementById(
		chartId + "Keyboard_accumulationchart_focus"
	);
	if (elem) return false;
	return (
		<div
			id={chartId + "Keyboard_accumulationchart_focus"}
			className="syncfusion-fix"
		/>
	);
}

function getComponent(theme, chart, specs, values) {
	let tile_theme = gestChartTheme(theme, chart, values);
	let chart_props = {
		chart,
		specs,
		values,
		theme: tile_theme,
	};
	if (chart.chartTypeId === 4)
		return <TargetChart {...chart_props} {...chart} theme={tile_theme} />;
	if (chart.chartTypeId === 7)
		return <NumberChart {...chart} {...values[0]} theme={tile_theme} />;
	if (chart.chartTypeId >= 9 && chart.chartTypeId <= 16)
		return <BasicChart {...chart_props} />;
	if (chart.chartTypeId >= 17 && chart.chartTypeId <= 18)
		return <AccumulationChart {...chart_props} />;
	if (chart.chartTypeId >= 19 && chart.chartTypeId <= 20)
		return <GaugeComponent {...chart_props} />;
	return false;
}

function getNbrVals(chart, values) {
	if ([9, 10, 12, 13, 14, 15, 16].indexOf(chart.chartTypeId) !== -1)
		return bar_chart_nbr_cols(values.series);
	return 0;
}

export function getChartSpecs(chart, chartTypeId, values) {
	let nbrVals = getNbrVals(chart, values);
	let chartTypesServices = [
		{ typesId: [9], services: [BarSeries], type: "Bar", nbrVals },
		{ typesId: [10], services: [ColumnSeries], type: "Column", nbrVals },
		{
			typesId: [12],
			services: [SplineAreaSeries],
			type: "SplineArea",
			nbrVals,
		},
		{
			typesId: [13],
			services: [StackingAreaSeries],
			type: "StackingArea",
			nbrVals,
		},
		{ typesId: [14], services: [AreaSeries], type: "Area", nbrVals },
		{
			typesId: [15],
			services: [StackingAreaSeries],
			type: "StackingArea100",
			nbrVals,
		},
		{
			typesId: [16],
			services: [StackingColumnSeries],
			type: "StackingColumn",
			nbrVals,
		},
		{ typesId: [17, 18], services: [PieSeries], type: "Pie", nbrVals },
		{ typesId: [19], services: [], type: "Pie", nbrVals },
		{ typesId: [20], services: [PieSeries], type: "Pie", nbrVals },
	];

	let ret = chartTypesServices.find(
		(a) => a.typesId.indexOf(chartTypeId) !== -1
	);
	return ret ? ret : false;
}

function gestChartTheme(theme, chart, values) {
	let color = theme === "light" ? "black" : "white";

	if (!values) return false;
	let ret = {
		...values,
		background: "transparent",
		legendSettings: {
			textStyle: {
				color: color,
				fontFamily: "Poppins-Light",
			},
		},
		subTitleStyle: {
			color: color,
		},
		labelStyle: {
			fontFamily: "Poppins-Light",
		},
	};

	if (ret.primaryXAxis)
		ret.primaryXAxis = {
			...ret.primaryXAxis,
			labelStyle: { color: color, fontFamily: "Poppins-Light" },
			titleStyle: { color: color, fontFamily: "Poppins-Light" },
		};

	if (ret.primaryYAxis)
		ret.primaryYAxis = {
			...ret.primaryYAxis,
			labelStyle: { color: color, fontFamily: "Poppins-Light" },
			titleStyle: { color: color, fontFamily: "Poppins-Light" },
		};

	if (ret.series && [17, 18].indexOf(chart.chartTypeId) === -1) {
		ret.series = ret.series.map((a, b) => {
			if (a.marker?.dataLabel) {
				if ([10].indexOf(chart.chartTypeId) === -1)
					a.marker.dataLabel.font.color = color;
				if (chart.chartTypeId === 13) {
					if (b < ret.series.length - 1)
						a.marker.dataLabel.font.color = color;
				}
				a.marker.dataLabel.font.fontFamily = "Poppins-Regular";
			}
			return a;
		});
	} else {
		ret.legendSettings = {
			...ret.legendSettings,
			position: "Bottom",
			alignment: "Center",
		};
	}
	delete ret.title;
	return ret;
}
